"use client";
import { motion, useScroll, useSpring } from "framer-motion";
import usePublicContacts, { phoneDisplay, phoneTel } from '@/hooks/usePublicContacts';
import {
    Shield, FileText, ChevronRight, Fingerprint, Lock, Mail,
    Building2, ListChecks, ScanFace, MapPin, Smartphone, Database, Share2,
    Baby, Clock, Scale, Trash2, Cookie, RefreshCw,
} from "lucide-react";
import { Fragment, useRef } from "react";
import Header from "@/components/landing/Header";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "@/components/landing/RichText";
import Footer from "@/components/landing/Footer";
import CTASection from "@/components/landing/CTASection";

/* Icons and anchor ids only — every readable field comes from
   `legal.privacy.sections`, index-aligned with this list (the i18n parity
   test pins the length). `delete-account` is the URL given to Google Play
   as the account-deletion link — do not rename it. */
const SECTION_META = [
    { id: "who-we-are", icon: Building2 },
    { id: "information-we-collect", icon: ListChecks },
    { id: "face-recognition", icon: ScanFace },
    { id: "location", icon: MapPin },
    { id: "app-permissions", icon: Smartphone },
    { id: "how-we-use", icon: Database },
    { id: "sharing", icon: Share2 },
    { id: "children", icon: Baby },
    { id: "retention", icon: Clock },
    { id: "security", icon: Shield },
    { id: "your-rights", icon: Scale },
    { id: "delete-account", icon: Trash2 },
    { id: "cookies", icon: Cookie },
    { id: "changes-contact", icon: RefreshCw },
];

/* Copy carries `{email}` / `{phone}` placeholders so the contact details
   always match SuperAdmin → Business Profile. Rendered as links. */
function Copy({ text, contacts }) {
    return text.split(/(\{email\}|\{phone\})/).map((part, i) => {
        if (part === "{email}") {
            return <a key={i} href={`mailto:${contacts.privacyEmail}`} className="font-bold text-[#2040CF] underline underline-offset-2 break-all">{contacts.privacyEmail}</a>;
        }
        if (part === "{phone}") {
            return <a key={i} href={`tel:${phoneTel(contacts.supportPhone)}`} className="font-bold text-[#2040CF] underline underline-offset-2 whitespace-nowrap">{phoneDisplay(contacts.supportPhone)}</a>;
        }
        return <Fragment key={i}>{part}</Fragment>;
    });
}

export default function PrivacyPolicy() {
    const contacts = usePublicContacts();
    const { t, tv } = useLanguage();
    const sections = tv("legal.privacy.sections").map((copy, i) => ({ ...copy, ...SECTION_META[i] }));

    const scrollRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: scrollRef });
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

    return (
        <div className="landing-root min-h-screen bg-[#FAFCFF]">
            <Header />
            <main className="min-h-screen bg-[#FAFCFF] selection:bg-[#3D5EE1]/10 selection:text-[#2040CF]">
            {/* Scroll Progress Bar */}
            <motion.div className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#2040CF] to-[#3D5EE1] z-[60] origin-left" style={{ scaleX }} />

            {/* Hero */}
            <section className="relative pt-24 pb-12 sm:pt-32 sm:pb-24 overflow-hidden bg-white border-b border-[#E8F4FD]">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#3D5EE1]/10 rounded-full blur-[120px] animate-pulse" />
                    <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-[#3D5EE1]/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: "2s" }} />
                    <div className="absolute inset-0 grid-bg opacity-[0.15]" />
                    <motion.div animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-[20%] left-[10%] opacity-20 hidden lg:block">
                        <Shield size={60} className="text-[#2040CF]" />
                    </motion.div>
                    <motion.div animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                        className="absolute bottom-[20%] right-[12%] opacity-20 hidden lg:block">
                        <Fingerprint size={80} className="text-[#2040CF]" />
                    </motion.div>
                    <motion.div animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-[40%] right-[25%] opacity-10 hidden lg:block">
                        <Lock size={40} className="text-[#2040CF]" />
                    </motion.div>
                </div>

                <div className="site-container relative z-10 px-3 sm:px-6">
                    <div className="flex flex-col items-center text-center">
                        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="font-sora font-black text-2xl sm:text-5xl md:text-6xl lg:text-[72px] text-[#0d1117] leading-[1.15] sm:leading-[1.1] tracking-tight mb-4 sm:mb-8">
                            <RichText text={t("legal.privacy.heading")} accentClassName="text-gradient-blue" />
                        </motion.h1>

                        <div className="w-24 h-1.5 bg-gradient-to-r from-[#2040CF] to-[#3D5EE1] rounded-full mb-8" />

                        <p className="text-xs sm:text-[17px] md:text-[19px] font-medium text-[#596875] max-w-[700px] leading-relaxed">
                            {t("legal.privacy.subtitle")}
                        </p>

                        <div className="mt-6 sm:mt-10 flex items-center gap-2 sm:gap-3 text-[11px] sm:text-[13px] font-bold text-[#94a3b8]">
                            <FileText size={13} className="sm:w-3.5 sm:h-3.5" />
                            <span>{t("legal.privacy.lastUpdated")}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content — plain (not fade-in) so the full text is readable
                immediately, including by store reviewers and crawlers. */}
            <section className="w-full py-10 sm:py-20 bg-[#FAFCFF]" ref={scrollRef}>
                <div className="site-container px-3 sm:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16">
                        <aside className="hidden lg:block lg:col-span-3 sticky top-32 h-fit">
                            <nav aria-label={t("legal.privacy.tocTitle")} className="flex flex-col gap-0.5 p-2 bg-white rounded-3xl border border-[#E8F4FD] shadow-sm">
                                <p className="px-5 pt-3 pb-2 text-[11px] font-black uppercase tracking-widest text-[#94a3b8]">{t("legal.privacy.tocTitle")}</p>
                                {sections.map((s) => (
                                    <a key={s.id} href={`#${s.id}`} className="flex items-center justify-between px-5 py-2.5 rounded-2xl hover:bg-[#EDF0FC] group transition-colors duration-300">
                                        <span className="font-sora font-bold text-[13px] text-[#4a5568] group-hover:text-[#1B35AC]">{s.title}</span>
                                        <ChevronRight size={14} className="text-[#94a3b8] group-hover:translate-x-1 group-hover:text-[#1B35AC] transition-all" />
                                    </a>
                                ))}
                            </nav>
                            <div className="mt-8 p-6 rounded-3xl bg-white border border-[#E8F4FD] shadow-sm">
                                <div className="w-10 h-10 rounded-xl bg-[#E8F4FD] flex items-center justify-center mb-4">
                                    <Mail size={18} className="text-[#2040CF]" />
                                </div>
                                <h4 className="font-sora font-bold text-[15px] text-[#0d1117] mb-2">{t("legal.privacy.contactCard.title")}</h4>
                                <p className="text-[13px] text-[#596875] leading-relaxed mb-4">{t("legal.privacy.contactCard.body")}</p>
                                <a href={`mailto:${contacts.privacyEmail}`} className="text-[13px] font-bold text-[#2040CF] break-all hover:underline">
                                    {contacts.privacyEmail}
                                </a>
                            </div>
                        </aside>

                        <div className="lg:col-span-9 flex flex-col gap-4 sm:gap-8">
                            {sections.map((section) => (
                                <div id={section.id} key={section.id}
                                    className="scroll-mt-28 bg-white rounded-[32px] p-6 sm:p-8 md:p-12 border border-[#E8F4FD] shadow-sm">
                                    <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                                        <div className="w-14 h-14 rounded-2xl bg-[#EDF0FC] flex items-center justify-center text-[#2040CF] flex-shrink-0">
                                            <section.icon size={24} />
                                        </div>
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <div className="inline-block w-fit px-3 py-1 rounded-full bg-[#E8F4FD] text-[#2040CF] font-black text-[10px] uppercase tracking-widest mb-4">{section.tag}</div>
                                            <h2 className="font-sora font-black text-2xl md:text-3xl text-[#0d1117] mb-4 tracking-tight">{section.title}</h2>
                                            {section.paragraphs.map((p, i) => (
                                                <p key={i} className="text-[15px] sm:text-[16px] leading-[1.8] text-[#596875] font-medium mb-4">
                                                    <Copy text={p} contacts={contacts} />
                                                </p>
                                            ))}
                                            {section.bullets.length > 0 && (
                                                <ul className="flex flex-col gap-3 mb-4">
                                                    {section.bullets.map((b, i) => (
                                                        <li key={i} className="p-4 rounded-2xl bg-[#FAFCFF] border border-[#E8F4FD]">
                                                            <p className="font-sora font-bold text-[14px] text-[#0d1117] mb-1">{b.term}</p>
                                                            <p className="text-[14px] leading-[1.7] text-[#596875]">
                                                                <Copy text={b.desc} contacts={contacts} />
                                                            </p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {section.after.map((p, i) => (
                                                <p key={i} className="text-[15px] sm:text-[16px] leading-[1.8] text-[#334155] font-medium mb-4 last:mb-0">
                                                    <Copy text={p} contacts={contacts} />
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="rounded-[40px] p-10 md:p-16 bg-[#0d1117] text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#3D5EE1]/10 rounded-full blur-[100px]" />
                                <div className="relative z-10 flex flex-col items-center text-center">
                                    <h3 className="font-sora font-black text-3xl md:text-4xl mb-6">
                                        <RichText text={t("legal.privacy.closing.heading")} accentClassName="text-[#7C93F0]" />
                                    </h3>
                                    <p className="text-white/70 text-[16px] mb-10 max-w-[520px] leading-relaxed">
                                        {t("legal.privacy.closing.body").replace("{email}", contacts.privacyEmail)}
                                    </p>
                                    <a href={`mailto:${contacts.privacyEmail}`} className="px-8 py-4 bg-white text-[#0d1117] rounded-2xl font-bold text-[14px] hover:shadow-xl hover:-translate-y-1 transition-all">
                                        {t("legal.privacy.closing.cta")}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
        <CTASection />
        <Footer />
        </div>
    );
}
